import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Alert, Button, ButtonWithSpinner, ChipInput, Switch, Text } from '@reveni-io/reveni-ui'

import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import CodeSnippet from '../CodeSnippet/CodeSnippet'
import EmbedStep from '../EmbedStep/EmbedStep'
import UrlParametersTable from '../UrlParametersTable/UrlParametersTable'
import EmbedSetupContentSkeleton from './EmbedSetupContentSkeleton/EmbedSetupContentSkeleton'
import useEmbedSetup from './hooks/useEmbedSetup/useEmbedSetup'
import { getEmbedSnippet, getUrlParametersSnippet } from './helpers'
import * as Styles from './styles'

const EmbedSetupContent = ({ storeId, storeProvider, onClose }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { hasPermission } = useUserPermissions()
  const canEdit = hasPermission(userPermissions.editOrderTrackingPage)
  const {
    gettingOrderTrackingEmbed,
    allowedDomains,
    handleChangeAllowedDomains,
    hideHeader,
    handleChangeHideHeader,
    canSave,
    savingOrderTrackingEmbed,
    handleSave,
  } = useEmbedSetup({ storeId, onSaved: onClose })

  if (gettingOrderTrackingEmbed) return <EmbedSetupContentSkeleton />

  return (
    <Styles.Container>
      <Styles.Header>
        <Text tag="h2" type="titleM">
          {t('Incrusta la Página de Seguimiento del Pedido en la página que elijas')}
        </Text>
      </Styles.Header>
      <Styles.Content>
        <Text type="bodyMRegular">
          {t('Sigue estos pasos para incrustar la página de seguimiento en cualquier página de tu web.')}
        </Text>
        <Styles.Steps>
          <EmbedStep
            number={1}
            description={t(
              'Indica el/los dominio(s) donde vas a incrustar la página de seguimiento. Por seguridad, solo esos dominios podrán cargarla.'
            )}
          >
            <Alert type="info">
              {t('Añade cada dominio donde quieras que aparezca la página de seguimiento. Se necesita al menos uno.')}
            </Alert>
            <ChipInput
              label={t('Dominio(s)')}
              value={allowedDomains}
              onChange={handleChangeAllowedDomains}
              helperText={t(
                'Pulsa Enter o coma para añadir. Pega una lista separada por comas para añadir varios a la vez'
              )}
            />
          </EmbedStep>
          <EmbedStep
            number={2}
            description={t(
              'Copia este código y pégalo dentro del <body> de la página donde quieras que aparezca la página de seguimiento. Su posición en el código determina su posición en la página.'
            )}
          >
            <CodeSnippet code={getEmbedSnippet(import.meta.env.VITE_REVENI_ORDER_TRACKING_PAGE_WIDGET)} />
          </EmbedStep>
          <EmbedStep
            number={3}
            description={t(
              'Incluye estos parámetros en la URL de tu página contenedora para que la página de seguimiento pueda cargar los datos correctos del pedido.'
            )}
            isLast
          >
            <CodeSnippet code={getUrlParametersSnippet(storeId)} />
            <UrlParametersTable storeProvider={storeProvider} />
          </EmbedStep>
        </Styles.Steps>
        <Styles.HideHeaderSection>
          <Styles.HideHeaderInfo>
            <Text type="bodyMMedium">{t('Ocultar cabecera de la página de seguimiento')}</Text>
            <Text type="bodySRegular" color={theme.colors.text.muted}>
              {t(
                'Oculta el logo y el título "Página de seguimiento" cuando se muestra embebida, para no duplicar tu propia navegación.'
              )}
            </Text>
          </Styles.HideHeaderInfo>
          <Switch checked={hideHeader} onChange={handleChangeHideHeader} />
        </Styles.HideHeaderSection>
      </Styles.Content>
      {!canEdit && (
        <Styles.PermissionAlert>
          <UnsavedChangesAlert hasPermission={false} />
        </Styles.PermissionAlert>
      )}
      <Styles.Footer>
        <Button variant="outlined" fullWidth onClick={onClose}>
          {t('Cancelar')}
        </Button>
        <ButtonWithSpinner
          fullWidth
          onClick={handleSave}
          defaultLabel={t('Guardar cambios')}
          loadingLabel={t('Guardando cambios')}
          loading={savingOrderTrackingEmbed}
          disabled={!canSave || !canEdit}
        />
      </Styles.Footer>
    </Styles.Container>
  )
}

export default EmbedSetupContent
